"use client"

import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { Circle } from "lucide-react"

import { cn } from "@/lib/utils"

// Context to pass error state from RadioGroup to RadioGroupItem
const RadioGroupContext = React.createContext<{
  "data-show-error"?: boolean
  "aria-invalid"?: boolean
}>({})

const RadioGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
    "data-show-error"?: boolean
    "aria-invalid"?: boolean
    "aria-describedby"?: string
  }
>(({ className, "data-show-error": showError, "aria-invalid": invalid, "aria-describedby": describedBy, ...props }, ref) => {
  const internalRef = React.useRef<HTMLDivElement>(null)

  // Forward the focus call to the checked item or first item
  React.useImperativeHandle(
    ref,
    () => {
      const root = internalRef.current
      if (!root) return null as any

      return {
        ...root,
        focus: () => {
          // Find the checked item, or fall back to the first item
          const checkedItem = root.querySelector('[role="radio"][aria-checked="true"]') as HTMLElement
          const firstItem = root.querySelector('[role="radio"]') as HTMLElement
          const itemToFocus = checkedItem || firstItem
          itemToFocus?.focus()
        }
      }
    },
    []
  )

  const contextValue = React.useMemo(() => ({
    "data-show-error": showError,
    "aria-invalid": invalid
  }), [showError, invalid])

  return (
    <RadioGroupContext.Provider value={contextValue}>
      <RadioGroupPrimitive.Root
        className={cn("grid gap-2 py-2", className)}
        data-show-error={showError}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        {...props}
        ref={internalRef}
      />
    </RadioGroupContext.Provider>
  )
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  const context = React.useContext(RadioGroupContext)
  const hasError = context["data-show-error"] && context["aria-invalid"]

  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "aspect-square h-4 w-4 rounded-full border text-primary ring-offset-background focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        hasError ? "border-destructive focus:ring-destructive" : "border-primary focus:ring-ring",
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="h-2.5 w-2.5 fill-current text-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
})
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem }
